/**
 * Operational defaults.
 *
 * These are seeds for the database-backed `settings` table (spec §52). Runtime
 * code reads the table so an operator can change policy without a deploy.
 * Secrets never live here.
 */

export const OPERATIONAL_DEFAULTS = {
	/** Anti-abuse safety limits, independent of the storage plan. */
	default_hourly_send_limit: 150,
	default_daily_send_limit: 1000,
	new_account_hourly_send_limit: 25,
	new_account_trust_days: 7,

	migration_concurrency: 2,
	migration_credential_ttl_hours: 24,
	migration_max_retries: 5,

	dns_check_interval_seconds: 300,
	dns_check_backoff_max_seconds: 3600,
	ownership_check_interval_seconds: 60,

	/** Deletion and retention. Nothing is purged inside these windows. */
	mailbox_deletion_retention_days: 30,
	/** Messages in Trash are permanently removed after this many days in Trash. */
	mail_trash_retention_days: 30,
	domain_removal_retention_days: 30,
	account_closure_retention_days: 60,

	/** Billing failure handling. Mail keeps flowing through the grace period. */
	payment_grace_period_days: 14,
	payment_retry_attempts: 4,
	restricted_after_grace_days: 7,
	suspend_after_restricted_days: 14,

	max_attachment_size_bytes: 50 * 1024 * 1024,
	support_email: "support@key.my",
	abuse_email: "abuse@key.my",
	status_page_url: "https://key.my/status",
} as const;

export type OperationalSettingKey = keyof typeof OPERATIONAL_DEFAULTS;

/** Account storage thresholds that drive customer-facing warnings. */
export const STORAGE_THRESHOLDS = {
	informational: 0.8,
	warning: 0.9,
	critical: 1.0,
} as const;

export const MAILBOX_PASSWORD_MIN_LENGTH = 12;
export const ACCOUNT_PASSWORD_MIN_LENGTH = 10;

/** Mail client settings surfaced on the "Set up on another device" screen. */
export const CLIENT_ENDPOINTS = {
	imap: { host: "mail.key.my", port: 993, security: "SSL/TLS" },
	submissionImplicit: { host: "mail.key.my", port: 465, security: "SSL/TLS" },
	submissionStartTls: { host: "mail.key.my", port: 587, security: "STARTTLS" },
	jmap: { url: "https://mail.key.my/.well-known/jmap" },
} as const;
